import Accordion from "@/common/components/Accordion";
import Box from "@/common/components/Box";
import SelectController from "@/common/components/SelectController";
import { useEquipmentCategories } from "@/common/hooks/queries/useEquipmentCategories";
import { useEquipmentManufacturers } from "@/common/hooks/queries/useEquipmentManufacturers";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches";
import { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { useParams } from "react-router-dom";
import { CreateScrapRuleSchema } from "../../schemas/maintenanceScrapRules/createScrapRule.schema";
import { useEquipmentModels } from "@/features/equipmentModels/hooks/useEquipmentModels";
import { useCompanyEquipments } from "@/common/hooks/queries/useCompanyEquipments";
import { EquipmentsTransformer } from "@/common/services/equipmentTransformer";
import { EquipmentCategoriesTransformer } from "@/common/services/equipmentCategoriesTransformer";
import { CompanyBranchesTransformer } from "@/common/services/companyBranchesTransformer";
import { EquipmentManufacturersTransformer } from "@/common/services/equipmentManufacturersTransformer";
import { EquipmentModelsTransformer } from "@/features/equipmentModels/services/equipmentModelsTransformer";

interface AccordionScrapRulesEquipmentsProps {
  register: UseFormRegister<CreateScrapRuleSchema>
  errors: FieldErrors<CreateScrapRuleSchema>
  control: Control<CreateScrapRuleSchema>
  disabled?: boolean
}

export default function AccordionScrapRulesEquipments({errors, control, disabled}: AccordionScrapRulesEquipmentsProps) {
  const { companyId } = useParams()

  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('')
  const [searchCompanyBranch, setSearchCompanyBranch] = useDebounce('')
  const [searchCompanyEquipments, setSearchCompanyEquipments] = useDebounce('')
  const [searchEquipmentManufacturers, setSearchEquipmentManufacturers] = useDebounce('')
  const [searchEquipmentModels, setSearchEquipmentModels] = useDebounce('')

  const { data: equipmentCategoryOptions, isPending: equipmentCategoryOptionsIsPending } = useEquipmentCategories({
    filters: { search: searchEquipmentCategory },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentCategoriesTransformer.optionsTransformer() }),
  })

  const { data: companyBranchesOptions, isPending: companyBranchesOptionsIsPending } = useAvailableCompanyBranches({
    companyId: companyId,
    filters: { search: searchCompanyBranch },
    mapper: Mapper.mapWithTransformer({ transformer: CompanyBranchesTransformer.optionsTransformer() }),
  })

  const { data: equipmentsOptions, isPending: equipmentsOptionsIsPending } = useCompanyEquipments({
    filters: { search: searchCompanyEquipments, companyId },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentsTransformer.optionsTransformer() }),
  })

  const { data: equipmentManufacturersOptions, isPending: equipmentManufacturersOptionsIsPending } = useEquipmentManufacturers({
    filters: { search: searchEquipmentManufacturers },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentManufacturersTransformer.optionsTransformer() }),
  })

  const { data: equipmentModelsOptions, isPending: equipmentModelsOptionsIsPending } = useEquipmentModels({
    filters: { search: searchEquipmentModels },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentModelsTransformer.optionsTransformer() }),
  })

  return (
    <Accordion
      name="scrapRulesEquipments"
      head={<p className="text-base">Equipamentos</p>}
      body={
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <SelectController
              control={control}
              name="equipment_category_ids"
              label="Categorias de equipamento"
              onInputChange={setSearchEquipmentCategory}
              options={equipmentCategoryOptions || []}
              isMulti
              dataTestid="equipment_category_ids_selector"
              errorMessage={errors?.equipment_category_ids?.message as string}
              isDisabled={equipmentCategoryOptionsIsPending || disabled}
              optionLabelFormat="icon"
            />
            <SelectController
              control={control}
              name="branch_ids"
              label="Filiais"
              onInputChange={setSearchCompanyBranch}
              options={companyBranchesOptions || []}
              isMulti
              dataTestid="branch_ids_selector"
              errorMessage={errors?.branch_ids?.message as string}
              isDisabled={companyBranchesOptionsIsPending || disabled}
              optionLabelFormat="icon"
            />
            <Box className="flex flex-col gap-4 bg-white-100 p-4">
              <SelectController
                control={control}
                name="manufacturer_ids"
                label="Fabricantes"
                onInputChange={setSearchEquipmentManufacturers}
                options={equipmentManufacturersOptions || []}
                isMulti
                dataTestid="equipment_manufacturer_ids_selector"
                errorMessage={errors?.manufacturer_ids?.message as string}
                isDisabled={equipmentManufacturersOptionsIsPending || disabled}
                optionLabelFormat="icon"
              />
              <SelectController
                control={control}
                name="equipment_model_ids"
                label="Modelos"
                onInputChange={setSearchEquipmentModels}
                options={equipmentModelsOptions || []}
                isMulti
                dataTestid="equipment_models_ids_selector"
                errorMessage={errors?.equipment_model_ids?.message as string}
                isDisabled={equipmentModelsOptionsIsPending || disabled}
                optionLabelFormat="icon"
              />
              <SelectController
                control={control}
                name="equipment_ids"
                label="Equipamentos na frota"
                onInputChange={setSearchCompanyEquipments}
                options={equipmentsOptions || []}
                isMulti
                dataTestid="equipments_ids_selector"
                errorMessage={errors?.equipment_ids?.message as string}
                isDisabled={equipmentsOptionsIsPending || disabled}
                optionLabelFormat="icon"
              />
            </Box>
          </div>
        </div>
      }
    />
  )
}
